import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Crown,
  Users,
  Sparkles,
  Check,
  X,
  MessageSquare,
  FileText,
  BarChart3,
  Shield,
  Zap,
  Heart,
  ShoppingCart,
  DollarSign,
  CalendarCheck,
  Scale,
  TrendingUp,
  AlertCircle,
  Lock,
  Building2,
  Phone,
  Gift,
  Copy,
  CheckCircle2,
  MessageCircle,
} from 'lucide-react';

/**
 * PricingSection - Displays three-tier access model
 *
 * Tiers:
 * 1. Champion (Lifetime Free Pro) - Early adopters/testers
 * 2. Freemium (Always Free) - Core voting rights + essentials
 * 3. Pro (Paid) - Ad-free + full features
 */

interface PricingTier {
  name: string;
  tagline: string;
  price: string;
  priceDetail?: string;
  icon: React.ComponentType<{ className?: string }>;
  features: Array<{
    text: string;
    included: boolean;
    icon?: React.ComponentType<{ className?: string }>;
  }>;
  cta: string;
  ctaVariant: 'default' | 'outline' | 'secondary';
  highlight?: boolean;
  badge?: string;
  borderColor?: string;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Champion',
    tagline: 'For Community Builders',
    price: 'Lifetime Free',
    priceDetail: 'Pro Access Forever',
    icon: Crown,
    badge: 'FOUNDING MEMBER',
    borderColor: 'border-amber-400',
    features: [
      { text: 'All Pro Features Unlocked', included: true, icon: Sparkles },
      { text: 'Lifetime Access (No Payment Ever)', included: true, icon: Heart },
      { text: 'Group Buying Power (25-50% Savings)', included: true, icon: ShoppingCart },
      { text: 'Vendor Discount Negotiations', included: true, icon: DollarSign },
      { text: 'Digital Treasury Access', included: true, icon: TrendingUp },
      { text: 'Facility Booking System', included: true, icon: CalendarCheck },
      { text: 'Champion Badge & Recognition', included: true, icon: Crown },
      { text: 'Early Access to New Features', included: true, icon: Zap },
      { text: 'Ad-Free Experience', included: true, icon: Shield },
    ],
    cta: 'Become a Champion',
    ctaVariant: 'default',
    highlight: true,
  },
  {
    name: 'Freemium',
    tagline: 'Always Free',
    price: '$0',
    priceDetail: 'Forever',
    icon: Users,
    features: [
      { text: 'Read Community Announcements', included: true, icon: Check },
      { text: 'View Public Documents', included: true, icon: Check },
      { text: 'Browse Event Calendar', included: true, icon: Check },
      { text: 'Basic Resident Profile', included: true, icon: Check },
      { text: 'Voting Rights', included: false, icon: Lock },
      { text: 'Private Messaging', included: false, icon: Lock },
      { text: 'Facility Booking', included: false, icon: Lock },
      { text: 'Group Buying Power', included: false, icon: Lock },
      { text: 'Vendor Discounts', included: false, icon: Lock },
      { text: 'Digital Treasury', included: false, icon: Lock },
      { text: 'Dispute Resolution', included: false, icon: Lock },
      { text: 'Community Crowdfunding', included: false, icon: Lock },
    ],
    cta: 'Stay Free',
    ctaVariant: 'outline',
  },
  {
    name: 'Pro',
    tagline: 'Premium Experience',
    price: '$2.50/month',
    priceDetail: 'billed annually at $30/year',
    icon: Sparkles,
    badge: 'BEST VALUE',
    borderColor: 'border-blue-400',
    features: [
      { text: 'Identity Verification Included', included: true, icon: Check },
      { text: 'Everything in Freemium, plus:', included: true, icon: Check },
      { text: 'Ad-Free Experience', included: true, icon: Shield },
      { text: 'Voting Rights (Identity Verified)', included: true, icon: Check },
      { text: 'Group Buying Power (Save 25-50%)', included: true, icon: ShoppingCart },
      { text: 'Vendor Discount Negotiations', included: true, icon: DollarSign },
      { text: 'Digital Treasury Management', included: true, icon: TrendingUp },
      { text: 'Facility Booking System', included: true, icon: CalendarCheck },
      { text: 'Dispute Resolution Tools', included: true, icon: Scale },
      { text: 'Community Crowdfunding Access', included: true, icon: Heart },
      { text: 'Unlimited Direct Messaging', included: true, icon: MessageSquare },
      { text: 'Unlimited Proposals', included: true, icon: FileText },
      // { text: 'Advanced Analytics & Reports', included: true, icon: BarChart3 },
      // { text: 'Priority Support', included: true, icon: Zap },
      // Added missing features per user request
      {
        text: 'Secure voting that ends disputes and builds trust in decisions',
        included: true,
        icon: Shield,
      },
      {
        text: 'Every vote produces a receipt you can check yourself',
        included: true,
        icon: Lock,
      },
      //  { text: 'Proposal system where every resident voice matters', included: true, icon: FileText },
      { text: 'Real-time voting results', included: true, icon: BarChart3 },
      {
        text: 'Keep money in your community with neighbor marketplace',
        included: true,
        icon: ShoppingCart,
      },
      {
        text: 'Approved Vendor portal for better service at lower costs',
        included: true,
        icon: DollarSign,
      },
      { text: 'Digital document vault - find anything in seconds', included: true, icon: FileText },
    ],
    cta: 'Start Free Trial',
    ctaVariant: 'default',
    highlight: true,
  },
];

type PricingSectionProps = {
  cityName?: string;
  /** Population count for display (e.g., "2.3 million" or "80,000+") */
  populationDisplay?: string;
  /** Phone number to call (e.g., "(713) 837-0311") */
  mayorPhoneNumber?: string;
  /** Phone number in tel: format (e.g., "+17138370311") */
  mayorPhoneTel?: string;
  /** Label for the phone line (e.g., "Houston 311 Help & Info") */
  mayorPhoneLabel?: string;
};

export function PricingSection({
  cityName = 'your city',
  populationDisplay = 'all residents',
  mayorPhoneNumber,
  mayorPhoneTel,
  mayorPhoneLabel = "Mayor's Office",
}: PricingSectionProps) {
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [scriptCopied, setScriptCopied] = useState(false);

  // The script residents should say when calling
  const callScript = `Hi, I'm a ${cityName} resident and I'm calling to ask the city to sponsor the ${cityName} Community App. It would give every resident free access to community tools, voting, and group buying. I think it would really benefit our community. Thank you!`;

  const handleCopyScript = async () => {
    try {
      await navigator.clipboard.writeText(callScript);
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy script:', err);
    }
  };

  // Handler for CTA button clicks
  const handleCtaClick = (tierName: string) => {
    if (tierName === 'Champion') {
      // Redirect to Champion application form
      window.location.href = '/apply-champion';
    } else {
      // All other plans (Freemium, Pro) redirect to interest form (waitlist)
      const interestFormSection = document.getElementById('interest-form');
      if (interestFormSection) {
        interestFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <section id="pricing" className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Choose Your Path to Participation</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Everyone gets <strong>1 month free Pro trial</strong> at launch. After that, choose the
            experience that fits your needs.
          </p>
        </div>

        {/* City Sponsorship Banner - Two Paths Comparison */}
        {mayorPhoneNumber && mayorPhoneTel && (
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 via-white to-blue-50 overflow-hidden">
              <CardContent className="p-0">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Gift className="w-5 h-5" />
                    <span className="font-bold text-lg">Want This App 100% Free?</span>
                  </div>
                  <p className="text-sm text-green-100">
                    If {cityName} sponsors this app, every resident gets Pro access forever. No
                    payment, ever.
                  </p>
                </div>

                {/* Two Path Comparison */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                  {/* Path A: You Pay */}
                  <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <Users className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <Badge variant="outline" className="text-xs">
                          Current Path
                        </Badge>
                      </div>
                    </div>
                    <h4 className="font-semibold text-lg mb-2">Organic (Self-Pay)</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      You pay <strong className="text-foreground">$2.50/month</strong> for Pro
                      access, or stay on limited Freemium.
                    </p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        <span>Full Pro features</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <X className="w-4 h-4 text-red-500" />
                        <span>You cover the cost</span>
                      </li>
                    </ul>
                  </div>

                  {/* Path B: City Sponsors */}
                  <div className="p-6 bg-green-50/50">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <Badge className="bg-green-600 text-white text-xs">
                          Best for Residents
                        </Badge>
                      </div>
                    </div>
                    <h4 className="font-semibold text-lg mb-2 text-green-800">City-Sponsored</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      The city sponsors the app, and{' '}
                      <strong className="text-green-700">
                        every resident gets Pro free forever
                      </strong>
                      .
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2 text-green-700">
                        <Check className="w-4 h-4" />
                        <span className="font-medium">Full Pro features</span>
                      </li>
                      <li className="flex items-center gap-2 text-green-700">
                        <Check className="w-4 h-4" />
                        <span className="font-medium">$0 forever for you</span>
                      </li>
                      <li className="flex items-center gap-2 text-green-700">
                        <Check className="w-4 h-4" />
                        <span className="font-medium">{populationDisplay} included</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="p-6 bg-gradient-to-r from-blue-50 to-green-50 border-t border-gray-200">
                  <div className="text-center mb-4">
                    <h4 className="font-bold text-lg mb-1">Make Your Voice Heard</h4>
                    <p className="text-sm text-muted-foreground max-w-xl mx-auto">
                      City leaders respond when residents speak up. One quick call can help make
                      this app free for everyone.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button
                      onClick={() => setIsCallModalOpen(true)}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors shadow-md h-auto"
                    >
                      <Phone className="w-5 h-5" />
                      <span>Call {cityName}</span>
                    </Button>
                    <div className="text-center sm:text-left">
                      <p className="text-sm font-medium text-foreground">{mayorPhoneNumber}</p>
                      <p className="text-xs text-muted-foreground">{mayorPhoneLabel}</p>
                    </div>
                  </div>

                  <p className="text-xs text-center text-muted-foreground mt-4 max-w-md mx-auto">
                    If thousands of residents call, the city will listen. Your voice can make this
                    app free for everyone in {cityName}.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Call Mayor Modal */}
        {mayorPhoneNumber && mayorPhoneTel && (
          <Dialog open={isCallModalOpen} onOpenChange={setIsCallModalOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader className="text-center sm:text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-blue-100 flex items-center justify-center mb-4">
                  <Phone className="w-8 h-8 text-green-600" />
                </div>
                <DialogTitle className="text-2xl">Call {mayorPhoneLabel}</DialogTitle>
                <DialogDescription className="text-base">
                  Your call matters! Here's everything you need.
                </DialogDescription>
              </DialogHeader>

              {/* Phone Number - Big and Prominent */}
              <div className="text-center py-4">
                <a
                  href={`tel:${mayorPhoneTel}`}
                  className="text-4xl font-bold text-green-600 hover:text-green-700 transition-colors"
                >
                  {mayorPhoneNumber}
                </a>
                <p className="text-sm text-muted-foreground mt-1">{mayorPhoneLabel}</p>
              </div>

              {/* What to Say Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold">What to Say</h4>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyScript}
                    className="h-8 px-3"
                  >
                    {scriptCopied ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-600 mr-1" />
                        <span className="text-green-600">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        <span>Copy</span>
                      </>
                    )}
                  </Button>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 italic">
                  "{callScript}"
                </div>
              </div>

              {/* Tips */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-600" />
                  Quick Tips
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Be polite and friendly, they're here to help</li>
                  <li>• Mention you're a {cityName} resident</li>
                  <li>• Keep it short and to the point</li>
                  <li>• If you get voicemail, leave a message!</li>
                </ul>
              </div>

              {/* Call Now Button */}
              <div className="flex flex-col gap-3 pt-2">
                <a
                  href={`tel:${mayorPhoneTel}`}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors shadow-lg text-lg"
                >
                  <Phone className="w-6 h-6" />
                  Call Now
                </a>
                <Button
                  variant="ghost"
                  onClick={() => setIsCallModalOpen(false)}
                  className="text-muted-foreground"
                >
                  I'll call later
                </Button>
              </div>

              {/* Social Proof */}
              <p className="text-xs text-center text-muted-foreground border-t pt-4">
                Every call counts. When enough residents speak up, city leaders take action.
              </p>
            </DialogContent>
          </Dialog>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {pricingTiers.map((tier) => {
            const Icon = tier.icon;
            return (
              <Card
                key={tier.name}
                className={`relative ${tier.highlight ? 'shadow-xl md:scale-105 lg:scale-110' : 'shadow-md'} ${tier.borderColor ? `border-2 ${tier.borderColor}` : ''} transition-transform md:hover:scale-105`}
              >
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge
                      variant="default"
                      className="bg-amber-500 text-white px-4 py-1 text-xs font-bold"
                    >
                      {tier.badge}
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center mb-4">
                    <Icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-2xl font-bold">{tier.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">{tier.tagline}</p>

                  <div className="mt-4">
                    <div className="text-3xl font-extrabold text-blue-600">{tier.price}</div>
                    {tier.priceDetail && (
                      <div className="text-sm text-muted-foreground mt-1">{tier.priceDetail}</div>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Features List */}
                  <ul className="space-y-3">
                    {tier.features.map((feature, idx) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <li
                          key={idx}
                          className={`flex items-start gap-3 ${feature.included ? 'text-foreground' : 'text-muted-foreground'}`}
                        >
                          {FeatureIcon && (
                            <FeatureIcon
                              className={`w-5 h-5 flex-shrink-0 mt-0.5 ${feature.included ? 'text-green-600' : 'text-red-500'}`}
                            />
                          )}
                          <span className="text-sm">{feature.text}</span>
                        </li>
                      );
                    })}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    variant={tier.ctaVariant}
                    className="w-full mt-6"
                    size="lg"
                    onClick={() => handleCtaClick(tier.name)}
                  >
                    {tier.cta}
                  </Button>

                  {/* Active Member Unlock for Freemium */}
                  {tier.name === 'Freemium' && (
                    <div className="mt-6 p-4 border-2 border-primary/30 rounded-lg bg-primary/5">
                      <div className="flex items-start gap-2 mb-3">
                        <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm">Want to vote?</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Upgrade to <strong>Active Member</strong> for a{' '}
                            <strong className="text-primary">one-time $3 fee</strong> to unlock
                            voting rights <strong>forever</strong>. Identity verification prevents
                            spam and fraud.
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full"
                        onClick={() => handleCtaClick('Freemium')}
                      >
                        Unlock Voting - $3 One-Time
                      </Button>
                      <p className="text-xs text-center text-muted-foreground mt-3">
                        ⚠️ Verification fee is non-refundable once ID verification completes
                      </p>
                      <a
                        href="/terms#refund-policy"
                        className="text-xs text-primary hover:underline block text-center mt-1"
                      >
                        View refund policy
                      </a>
                    </div>
                  )}

                  {/* Special Note for Pro */}
                  {tier.name === 'Pro' && (
                    <>
                      <div className="mt-4 p-3 bg-muted/50 rounded-md border border-muted">
                        <p className="text-xs text-muted-foreground">
                          <strong>Includes identity verification</strong> - First-time subscribers
                          complete one-time ID verification ($3 non-refundable fee included in $30
                          annual price).
                        </p>
                      </div>

                      <div className="text-center mt-4 pt-4 border-t border-muted">
                        <p className="text-xs text-muted-foreground mb-2">
                          Or pay monthly at <strong className="text-foreground">$10/month</strong>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          (Annual plan saves you <strong className="text-green-600">75%</strong>)
                        </p>
                      </div>

                      <p className="text-xs text-center text-muted-foreground mt-4">
                        30-day money-back guarantee (minus $3 non-refundable verification fee).{' '}
                        <a href="/terms#refund-policy" className="text-primary hover:underline">
                          View refund policy
                        </a>
                      </p>

                      <p className="text-xs text-center text-muted-foreground mt-3">
                        Your subscription keeps the platform ad-free, secure, and continuously
                        improving for everyone.
                      </p>
                    </>
                  )}

                  {/* Special Note for Champions */}
                  {tier.name === 'Champion' && (
                    <p className="text-xs text-center text-muted-foreground mt-4 italic">
                      Thank you for building {cityName}! Your contributions earn lifetime access.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* What Your Payment Supports */}
        <div className="mt-16 max-w-4xl mx-auto">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-center mb-6">
                What Pro Subscriptions Support
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">Ad-Free & Spam-Free</h4>
                    <p className="text-sm text-muted-foreground">
                      No intrusive ads, no spam, no tracking pixels. Just community.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Zap className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">Security & Continuity</h4>
                    <p className="text-sm text-muted-foreground">
                      SSL certificates, encryption, server costs, uptime monitoring.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Sparkles className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">New Features</h4>
                    <p className="text-sm text-muted-foreground">
                      Continuous development of community-requested tools and improvements.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Users className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">Moderation & Safety</h4>
                    <p className="text-sm text-muted-foreground">
                      Human moderators, abuse prevention, spam filtering, community safety.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trial Reminder */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-gradient-to-r from-blue-100 to-purple-100 rounded-full px-6 py-3">
            <p className="text-sm font-semibold">
              🎉 <strong>All residents get 1 month free Pro trial</strong> when {cityName} launches
            </p>
          </div>
          <p className="text-sm text-muted-foreground mt-4 max-w-2xl mx-auto">
            Experience the full platform before deciding. Voting rights are always free. Choose Pro
            only if you want the premium experience.
          </p>
        </div>
      </div>
    </section>
  );
}
